import { action, computed, observable, reaction, makeObservable } from 'mobx';
import {Disposers} from '../../../../../../lib/helpers';
import {StudentPlayerState, TimeRange} from '../models';
import {isTimeRangesEqual} from '../utils';
import {AudioElementStore} from './AudioElementStore';

function getPosX(event: TouchEvent | MouseEvent): number {
  if (event instanceof MouseEvent) {
    return event.pageX || event.clientX;
  }
  else {
    return event.touches[0].pageX || event.touches[0].clientX;
  }
}


export class AudioProgressStore {
  @observable.ref
  buffered: readonly TimeRange[] = [];

  @observable
  duration: number = 0;

  @observable
  currentTime: number = 0;

  @observable
  dragCount: number = 0;

  @observable
  draggingTime: number | null = null;

  private elementStore: AudioElementStore | null = null;
  private progressElement: HTMLDivElement | null = null;

  private readonly disposers = new Disposers();

  private readonly dragDisposers = new Disposers();

  constructor() {
    makeObservable(this);
  }

  syncAudioDurationFrom(element: AudioElementStore) {
    this.disposers.add(reaction(
      () => element.duration,
      () => this.duration = element.duration,
      {fireImmediately: true}
    ));
  }

  syncBufferedFrom(elementStore: AudioElementStore) {
    this.disposers.add(reaction(
      () => elementStore.buffered,
      () => this.updateBuffered(elementStore.buffered),
      {fireImmediately: true}
    ));
  }

  syncCurrentTimeFrom(elementStore: AudioElementStore) {
    this.disposers.add(reaction(
      () => elementStore.currentTime,
      () => this.currentTime = elementStore.currentTime,
      {fireImmediately: true}
    ));
  }

  syncStudentState(func: () => StudentPlayerState | null) {
    this.disposers.add(reaction(
      func,
      (state) => {
        if (state === null) {
          this.updateBuffered([]);
          this.currentTime = 0;
        }
        else {
          this.updateBuffered(state.buffered);
          this.currentTime = state.currentTime;
        }
      },
      {fireImmediately: true}
    ));
  }

  updateCurrentTimeOnDrag(elementStore: AudioElementStore, progressElement: HTMLDivElement) {
    this.elementStore = elementStore;
    this.progressElement = progressElement;
    progressElement.addEventListener('mousedown', this.beginDragTime);
    progressElement.addEventListener('touchstart', this.beginDragTime);
    this.disposers.add(() => {
      progressElement.removeEventListener('mousedown', this.beginDragTime);
      progressElement.removeEventListener('touchstart', this.beginDragTime);
    });
  }

  // Get time info while dragging indicator by mouse or touch
  private getDraggingTime = (event: MouseEvent | TouchEvent): number | null => {
    const progressElement = this.progressElement;

    if (this.duration === 0 || progressElement === null)
      return null;

    const progressRect = progressElement.getBoundingClientRect();
    const maxRelativePos = progressRect.width;
    let relativePos = getPosX(event) - progressRect.left;

    if (relativePos < 0)
      relativePos = 0;
    if (relativePos > maxRelativePos)
      relativePos = maxRelativePos;
    return (this.duration * relativePos) / maxRelativePos;
  }

  @action
  private beginDragTime = (event: MouseEvent | TouchEvent): void => {
    event.stopPropagation();
    this.draggingTime = this.getDraggingTime(event);

    if (this.draggingTime === null)
      return;

    window.addEventListener('mousemove', this.moveDraggingTime);
    window.addEventListener('touchmove', this.moveDraggingTime);
    window.addEventListener('mouseup', this.stopDraggingTime);
    window.addEventListener('touchend', this.stopDraggingTime);
    this.dragDisposers.add(() => {
      window.removeEventListener('mousemove', this.moveDraggingTime);
      window.removeEventListener('touchmove', this.moveDraggingTime);
      window.removeEventListener('mouseup', this.stopDraggingTime);
      window.removeEventListener('touchend', this.stopDraggingTime);
    });
  }

  @action
  private moveDraggingTime = (event: MouseEvent | TouchEvent): void => {
    event.preventDefault();
    event.stopPropagation();
    // Prevent Chrome drag selection bug
    const windowSelection: Selection | null = window.getSelection();
    if (windowSelection && windowSelection.type === 'Range')
      windowSelection.empty();

    this.draggingTime = this.getDraggingTime(event);
  }

  @action
  private stopDraggingTime = (event: MouseEvent | TouchEvent): void => {
    event.preventDefault();
    event.stopPropagation();

    this.dragCount++;
    this.draggingTime = null;
    if (this.elementStore === null)
      return;
    const draggedTime = this.getDraggingTime(event);
    if (draggedTime !== null)
      this.elementStore.setCurrentTime(draggedTime);

    this.dragDisposers.dispose();
  }

  @computed
  get canShowProgress(): boolean {
    return this.duration > 0;
  }

  @action
  private updateBuffered = (buffered: readonly TimeRange[]) => {
    if (isTimeRangesEqual(buffered, this.buffered))
      return;
    this.buffered = buffered;
  };

  dispose() {
    this.dragDisposers.dispose();
    this.disposers.dispose();
  }
}
